// Fetches Deribit's live dated-futures curve for a token via the one-shot
// `futures_snapshot` request (added to TEST 2026-07-10) — a genuinely
// independent, real market-quoted forward curve (mark_price per dated
// instrument), unlike the vol surface's Future column which is itself
// derived from the same options chain used for Base Discount (see
// build-carry-curve.ts's Basis tautology caveat). This is the "independent
// source" that caveat says is missing for a real Basis signal.
//
// Response shape: { futures_snapshot: { token_pair, futures: [...] } } — one
// PERPETUAL entry plus ~12 dated instruments, each carrying index_price/
// mark_price/last_price/settlement_price/stats. No expiry field is returned;
// expiry is parsed from instrument_name (e.g. "BTC-24JUL26") by
// parse-deribit-expiry.ts. The PERPETUAL entry also carries funding_8h (the
// current/predicted 8h funding rate) — dated instruments don't have one.
//
// Polled every 30s, matching use-vol-surface.ts's cadence.
import { useContext, useEffect, useRef, useState } from 'react';

import { WebSocketContext } from '../websocket-context/websocket-provider';

export interface FuturesSnapshotInstrument {
  instrument_name: string;
  index_price: number | null;
  mark_price: number | null;
  last_price: number | null;
  settlement_price: number | null;
  timestamp: number | null; // Unix epoch ms — when THIS tick was captured, not an expiry
  funding_8h: number | null; // PERPETUAL only — current/predicted 8h funding rate, decimal
}

const POLL_MS = 30_000;

export const useFuturesSnapshot = (
  tokenPair: string,
): {
  futures: FuturesSnapshotInstrument[] | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
} => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const [futures, setFutures] = useState<FuturesSnapshotInstrument[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  const wsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  useEffect(() => {
    wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };
  });

  useEffect(() => {
    if (!tokenPair || !isConnected) return;
    let cancelled = false;

    const fetchSnapshot = () => {
      const ws = wsRef.current;
      if (!ws.sendMessage || !ws.subscribeToChannel) return;

      const reqId = `FUTURES_SNAPSHOT_${tokenPair}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      let settled = false;
      const finish = () => {
        settled = true;
        const unsub = wsRef.current.unsubscribeFromChannel;
        if (unsub) {
          try {
            unsub(reqId);
          } catch {
            /* ignore */
          }
        }
      };

      ws.subscribeToChannel(reqId, (raw: unknown) => {
        if (cancelled || settled) return;
        const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        if (resp?.error) {
          setError(`futures_snapshot error: ${JSON.stringify(resp.error)}`);
          setIsLoading(false);
          finish();
          return;
        }
        const result = resp?.result;
        const snapshot =
          result && typeof result === 'object'
            ? (result as Record<string, unknown>).futures_snapshot
            : undefined;
        const list =
          snapshot && typeof snapshot === 'object'
            ? (snapshot as Record<string, unknown>).futures
            : undefined;
        if (!Array.isArray(list)) {
          setError('futures_snapshot: unexpected response shape');
          setIsLoading(false);
          finish();
          return;
        }
        setFutures(list as FuturesSnapshotInstrument[]);
        setError(null);
        setIsLoading(false);
        setLastUpdated(Date.now());
        finish();
      });

      ws.sendMessage(
        JSON.stringify({
          jsonrpc: '2.0',
          id: reqId,
          method: 'futures_snapshot',
          params: { token_pair: tokenPair },
        }),
      );

      setTimeout(() => {
        if (cancelled || settled) return;
        setError('futures_snapshot timed out');
        setIsLoading(false);
        finish();
      }, 15000);
    };

    setIsLoading(true);
    fetchSnapshot();
    const interval = setInterval(fetchSnapshot, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [tokenPair, isConnected]);

  return { futures, isLoading, error, lastUpdated };
};
